import { Button } from "@/components/common/Button/Button"
import { Typography } from "@/components/common/Typography/Typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconLoader2, IconProps } from "@tabler/icons-react"
import { createPortal } from "react-dom"

const SpinningLoader: React.FC<IconProps> = (props) => (
  <IconLoader2 {...props} className="animate-spin" />
)

interface ConfirmDialogProps {
  title: string
  /** Optional when `details` carries the content. */
  message?: string
  /** Structured content under the message, e.g. a list of recipients. Left-aligned
   *  and scrolling, where a long `message` string would be one centered paragraph. */
  details?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  onConfirm: () => void
  onCancel: () => void
  icon?: React.ComponentType<IconProps>
  variant?: "danger" | "warning" | "info"
  isLoading?: boolean
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  message,
  details,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  icon: Icon,
  variant = "danger",
  isLoading = false,
}) => {
  const { translate } = useTranslation()

  const iconBg = {
    danger: "bg-red1/10",
    warning: "bg-yellow1/10",
    info: "bg-grey3",
  }[variant]

  const iconColor = {
    danger: "text-red1",
    warning: "text-yellow1",
    info: "text-grey5",
  }[variant]

  const dialog = (
    // Portalled to document.body, but React still bubbles synthetic events
    // through the COMPONENT tree, not the DOM tree — without this, a click on
    // Cancel/Confirm keeps bubbling up to whatever ancestor rendered the
    // trigger (e.g. a table row with its own onClick), firing that too.
    <div
      className="fixed inset-0 z-100 flex items-center justify-center p-4"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />

      {/* Dialog */}
      <div className="relative z-10 w-full max-w-sm rounded-xl border border-grey4 bg-grey2 p-6 shadow-2xl">
        {/* Icon */}
        <div className="mb-4 flex justify-center">
          <div className={`rounded-full ${iconBg} p-4`}>
            {Icon ? (
              <Icon size={28} className={iconColor} strokeWidth={2} />
            ) : null}
          </div>
        </div>

        {/* Content */}
        <Typography
          variant="textM"
          as="h3"
          className="mb-3 text-center font-semibold text-white"
        >
          {title}
        </Typography>
        {message && (
          <Typography
            variant="textS"
            as="p"
            className="mb-6 text-center leading-relaxed text-grey5"
          >
            {message}
          </Typography>
        )}
        {details && (
          <div className="mb-6 max-h-60 overflow-y-auto text-left">
            {details}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2.5">
          <Button
            label={cancelLabel}
            title={cancelLabel}
            onClick={onCancel}
            variantColor="dark"
            variantSize="small"
            isFullWidth
            isDisabled={isLoading}
          />
          <Button
            label={
              isLoading ? translate("CONFIRM_DIALOG/PROCESSING") : confirmLabel
            }
            title={isLoading ? undefined : confirmLabel}
            IconLeft={isLoading ? SpinningLoader : undefined}
            onClick={onConfirm}
            variantColor="yellow"
            variantSize="small"
            isFullWidth
            isDisabled={isLoading}
          />
        </div>
      </div>
    </div>
  )

  if (typeof document === "undefined") {
    return dialog
  }

  return createPortal(dialog, document.body)
}
